/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Text } from "@vector-im/compound-web";
import {
  CheckIcon,
  CopyIcon,
} from "@vector-im/compound-design-tokens/assets/web/icons";
import { type Room } from "matrix-js-sdk";
import copy from "copy-to-clipboard";
import { useTranslation } from "react-i18next";

import {
  buildInviteLinkUrl,
  getMyInviteToken,
  rotateMyInviteToken,
  RoomInviteLinkRequestError,
  type RoomInviteToken,
} from "../invite/roomInviteLink";
import { getHomeserverAssociatedDomain } from "../knownHomeserversMap";
import { Modal } from "../Modal";
import styles from "./RoomInviteLinkModal.module.css";
import { canManageRoomInviteLink } from "./useCanManageRoomInviteLink";

interface Props {
  room: Room;
  open: boolean;
  onDismiss: () => void;
}

/** Модалка просмотра, копирования и обновления invite gateway-ссылки. */
export const RoomInviteLinkModal: FC<Props> = ({ room, open, onDismiss }) => {
  const { t } = useTranslation();
  const [token, setToken] = useState<RoomInviteToken>();
  const [error, setError] = useState<string>();
  const [isLoading, setIsLoading] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const loadRequestedRef = useRef(false);
  const mountedRef = useRef(true);
  const copyResetTimerRef = useRef<number | undefined>(undefined);
  // Пока модалка закрыта, вычисление прав и загрузка токена не нужны.
  const canManage = open && canManageRoomInviteLink(room);

  const inviteUrl = useMemo(() => {
    if (!token) return undefined;
    const webDomain = getHomeserverAssociatedDomain(
      room.client.getDomain() ?? "",
      "web",
    );
    const baseUrl = `https://${webDomain}`;
    return buildInviteLinkUrl(baseUrl, token.value);
  }, [room.client, token]);

  useEffect(() => {
    mountedRef.current = true;
    return (): void => {
      mountedRef.current = false;
      window.clearTimeout(copyResetTimerRef.current);
    };
  }, []);

  const applyToken = useCallback((nextToken: RoomInviteToken): void => {
    setToken(nextToken);
    setIsCopied(false);
  }, []);

  const loadToken = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    setError(undefined);
    try {
      const response = await getMyInviteToken(room.client, room.roomId);
      if (mountedRef.current) applyToken(response.token);
    } catch (error) {
      if (mountedRef.current) {
        setError(
          getErrorMessage(
            error,
            t("room_invite_link_modal.temporary_error"),
            t("room_invite_link_modal.load_error"),
          ),
        );
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [applyToken, room.client, room.roomId, t]);

  useEffect(() => {
    if (!open || loadRequestedRef.current) return;
    loadRequestedRef.current = true;
    void loadToken();
  }, [loadToken, open]);

  const onCopy = useCallback((): void => {
    if (!inviteUrl || !canManage || !copy(inviteUrl)) return;

    setIsCopied(true);
    window.clearTimeout(copyResetTimerRef.current);
    copyResetTimerRef.current = window.setTimeout(
      () => setIsCopied(false),
      2000,
    );
  }, [canManage, inviteUrl]);

  const onRotate = useCallback(async (): Promise<void> => {
    if (!canManage || isRotating) return;

    setIsRotating(true);
    setError(undefined);
    try {
      const response = await rotateMyInviteToken(room.client, room.roomId);
      if (mountedRef.current) applyToken(response.token);
    } catch (error) {
      if (mountedRef.current) {
        setError(
          getErrorMessage(
            error,
            t("room_invite_link_modal.temporary_error"),
            t("room_invite_link_modal.load_error"),
          ),
        );
      }
    } finally {
      if (mountedRef.current) setIsRotating(false);
    }
  }, [applyToken, canManage, isRotating, room.client, room.roomId, t]);

  return (
    <Modal
      title={t("room_invite_link_modal.title")}
      open={open}
      onDismiss={onDismiss}
      className={styles.modal}
    >
      <div className={styles.content}>
        <Text className={styles.description} size="md">
          {t("room_invite_link_modal.description")}
        </Text>

        {error && (
          <Text className={styles.error} role="alert" size="md">
            {error}
          </Text>
        )}

        {isLoading && !inviteUrl && (
          <div className={styles.linkBlock} aria-label={t("common.loading")}>
            …
          </div>
        )}

        {inviteUrl && (
          <div className={styles.linkBlock}>
            <Text className={styles.linkValue} size="md" weight="medium">
              {inviteUrl}
            </Text>
            <button
              type="button"
              className={styles.copyButton}
              aria-label={
                isCopied
                  ? t("room_invite_link_modal.copied")
                  : t("room_invite_link_modal.copy")
              }
              onClick={onCopy}
              disabled={!canManage}
            >
              {isCopied ? <CheckIcon /> : <CopyIcon />}
            </button>
          </div>
        )}

        {!canManage && (
          <Text className={styles.hint} size="md">
            {t("room_invite_link_modal.permission_hint")}
          </Text>
        )}

        {error && !inviteUrl && !isLoading && (
          <Button kind="secondary" onClick={() => void loadToken()}>
            {t("room_invite_link_modal.retry")}
          </Button>
        )}

        <div className={styles.actions}>
          <Button
            kind="secondary"
            onClick={() => void onRotate()}
            disabled={!canManage || isRotating || !token}
          >
            {isRotating
              ? t("room_invite_link_modal.rotating")
              : t("room_invite_link_modal.rotate")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

function getErrorMessage(
  error: unknown,
  temporaryMessage: string,
  defaultMessage: string,
): string {
  if (
    error instanceof RoomInviteLinkRequestError &&
    (error.status === undefined || error.status >= 500)
  ) {
    return temporaryMessage;
  }
  return defaultMessage;
}
